import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertCircle, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { getAnalysisResults, getAnalysisStatus } from '../services/scriptService';
import { getScorecardById } from '../services/scorecardService';
import { AnalysisResults, Scorecard, Blueprint, hasDeterministicPillarScores, isNucliusBlueprint, NucliusScorecardSections } from '../types/analysis';
import NucliusScorecardReport from '@/components/scorecard/NucliusScorecardReport';
import IndexWorkspaceShell from '@/components/index/IndexWorkspaceShell';
import ForensicScorecard from '@/components/scorecard/ForensicScorecard';
import MandateOverviewPanel from '@/components/scorecard/MandateOverview';
import {
  formatOriginalityDisplay,
  isOriginalityExcluded,
} from '@/utils/originalityDisplay';
import { onIndexShortcut } from '@/hooks/useIndexShortcuts';
import { BlueprintMarkdown } from '../components/BlueprintMarkdown';
import { CollapsibleSection } from '../components/CollapsibleSection';
import { VantageScorecard } from '../components/scorecard/VantageScorecard';
import { downloadAsText, downloadAsDocx } from '../utils/scorecardExport';
import { exportForensicScorecardPdf, exportNucliusScorecardPdf } from '@/utils/forensicScorecardPdf';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

/** Full agent output section — from blueprint.sections.full or trailing blueprint markdown. */
function resolveFullAnalysisMarkdown(sectionFull: string, blueprintMarkdown?: string): string {
  if (sectionFull?.trim()) return sectionFull.trim();
  if (!blueprintMarkdown?.trim()) return '';
  const idx = blueprintMarkdown.search(/# Full analysis output/i);
  if (idx >= 0) return blueprintMarkdown.slice(idx).trim();
  return '';
}

const ScriptAnalysis: React.FC = () => {
  const { sessionId, scorecardId } = useParams<{ sessionId?: string; scorecardId?: string }>();
  const navigate = useNavigate();
  
  const [results, setResults] = useState<AnalysisResults | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [includeTechnicalAppendix, setIncludeTechnicalAppendix] = useState(false);
  const [useForensicView, setUseForensicView] = useState(true);

  useEffect(() => {
    return onIndexShortcut('export', () => {
      const sc = results?.scorecard;
      if (!sc || !results) {
        setDownloadDialogOpen(true);
        return;
      }
      const decisionMaker = results.audience_type !== 'creator';
      const deterministic = hasDeterministicPillarScores(sc);
      const bp =
        results.blueprint && typeof results.blueprint === 'object'
          ? (results.blueprint as Blueprint)
          : undefined;
      if (decisionMaker && isNucliusBlueprint(bp)) {
        exportNucliusScorecardPdf({
          results,
          scriptTitle: results.script_title || 'Untitled screenplay',
          sessionId: sessionId || scorecardId || '',
        });
      } else if (decisionMaker && deterministic && useForensicView) {
        exportForensicScorecardPdf({
          scorecard: sc,
          scriptTitle: results.script_title || 'Untitled screenplay',
          sessionId: sessionId || scorecardId || '',
        });
      } else {
        setDownloadDialogOpen(true);
      }
    });
  }, [results, useForensicView, sessionId, scorecardId]);

  useEffect(() => {
    const id = sessionId || scorecardId;
    if (!id) {
      setError('Session ID or Scorecard ID is required');
      setLoading(false);
      return;
    }

    const fetchResults = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // If scorecardId is provided, fetch from scorecard storage
        if (scorecardId) {
          const scorecardDetail = await getScorecardById(scorecardId);
          // Convert scorecard detail to AnalysisResults format (include audience_type and detailed analysis for retrieval)
          const nestedBm =
            scorecardDetail.blueprint &&
            typeof scorecardDetail.blueprint === 'object' &&
            'blueprint_markdown' in scorecardDetail.blueprint
              ? (scorecardDetail.blueprint as { blueprint_markdown?: string }).blueprint_markdown
              : undefined;
          setResults({
            script_title: scorecardDetail.script_title || null,
            parsed_script: null, // Scorecard storage doesn't include parsed_script
            scorecard: scorecardDetail.scorecard || null,
            blueprint: scorecardDetail.blueprint || null,
            blueprint_markdown: scorecardDetail.blueprint_markdown ?? nestedBm ?? null,
            audience_type: scorecardDetail.audience_type ?? null,
            etb_analysis: scorecardDetail.etb_analysis ?? null,
            conceptual_analysis: scorecardDetail.conceptual_analysis ?? null,
            emotional_analysis: scorecardDetail.emotional_analysis ?? null,
            genre_analysis: scorecardDetail.genre_analysis ?? null,
            scene_analysis: scorecardDetail.scene_analysis ?? null,
            dialogue_analysis: scorecardDetail.dialogue_analysis ?? null,
            mandate_overview: scorecardDetail.mandate_overview ?? null,
          } as AnalysisResults);
          setLoading(false);
          return;
        }
        
        // Otherwise, use session-based polling (existing flow)
        if (sessionId) {
          let attempts = 0;
          const maxAttempts = 60;
          
          while (attempts < maxAttempts) {
            const status = await getAnalysisStatus(sessionId);
            
            if (status.status === 'completed') {
              const analysisResults = await getAnalysisResults(sessionId);
              setResults(analysisResults);
              setLoading(false);
              return;
            } else if (status.status === 'error') {
              throw new Error(status.error || 'Analysis failed');
            }
            
            // Wait before next poll
            await new Promise(resolve => setTimeout(resolve, 2000));
            attempts++;
          }
          
          throw new Error('Analysis timeout - maximum polling attempts reached');
        }
      } catch (err: any) {
        console.error('Error fetching analysis results:', err);
        setError(err.message || 'Failed to load analysis results');
        setLoading(false);
      }
    };

    fetchResults();
  }, [sessionId, scorecardId]);

  if (loading) {
    return (
      <IndexWorkspaceShell activeNav="reports">
        <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-index-accent border-t-transparent" />
          <p className="text-white/45 font-sans text-sm">Loading audit results…</p>
        </div>
      </IndexWorkspaceShell>
    );
  }

  if (error) {
    const scorecardPending =
      /scorecard has not been assembled|scorecard_pending|Scorecard not assembled/i.test(error);
    return (
      <IndexWorkspaceShell activeNav="reports">
        <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
          <AlertCircle className="h-12 w-12 text-index-risk" />
          <div className="text-center max-w-lg px-4">
            <h2 className="text-xl font-sans font-bold mb-2 text-white">
              {scorecardPending ? 'Scorecard not ready' : 'Error loading results'}
            </h2>
            <p className="text-white/45 mb-4 font-sans text-sm">{error}</p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              {scorecardPending && sessionId && (
                <Button
                  variant="indexPrimary"
                  onClick={() => navigate(`/workflow/steps/${sessionId}`)}
                >
                  Review step outputs
                </Button>
              )}
              <Button variant="indexOutline" onClick={() => navigate('/workflow')}>
                Open NucliusAudit
              </Button>
            </div>
          </div>
        </div>
      </IndexWorkspaceShell>
    );
  }

  if (!results) {
    return (
      <IndexWorkspaceShell activeNav="reports">
        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <p className="text-white/45 font-sans text-sm">No results available</p>
        </div>
      </IndexWorkspaceShell>
    );
  }

  const scorecard = results.scorecard;
  const audienceType = results.audience_type ?? undefined;
  const isCreator = audienceType === 'creator';
  const useDeterministicScores = scorecard ? hasDeterministicPillarScores(scorecard) : false;
  // Prefer v7_scores; fall back to legacy v6_scores for scorecards saved before the V7 rename.
  const vs = scorecard?.v7_scores ?? scorecard?.v6_scores;
  const blueprintMarkdown =
    results.blueprint_markdown ||
    (results.blueprint && typeof results.blueprint === 'object' && 'blueprint_markdown' in results.blueprint
      ? (results.blueprint as { blueprint_markdown?: string }).blueprint_markdown
      : undefined);
  const hasBlueprintMd = typeof blueprintMarkdown === 'string' && blueprintMarkdown.trim().length > 0;
  const blueprintObj =
    results.blueprint && typeof results.blueprint === 'object'
      ? (results.blueprint as Blueprint)
      : undefined;
  const blueprintSections = blueprintObj?.sections ?? undefined;
  const showNucliusReport = isNucliusBlueprint(blueprintObj);
  const nucliusSections: NucliusScorecardSections | undefined = showNucliusReport
    ? {
        story_overview: blueprintSections?.story_overview,
        mandate: blueprintSections?.mandate,
        engagement: blueprintSections?.engagement,
        commerciality: blueprintSections?.commerciality,
        recommendation: blueprintSections?.recommendation,
        full: blueprintSections?.full,
      }
    : undefined;
  const sectionScorecard = blueprintSections?.scorecard?.trim() || '';
  const sectionSummary = blueprintSections?.summary?.trim() || '';
  const sectionFull = blueprintSections?.full?.trim() || '';
  const hasSections = !!(sectionScorecard || sectionSummary || sectionFull);
  // Legacy 3-section layout (older echo_index_v7 scorecards).
  const showThreeSection = isCreator && hasSections && !showNucliusReport;
  const showCreatorBlueprintOnly = isCreator && hasBlueprintMd && !showNucliusReport;
  const showVantageScorecard = !!(scorecard && useDeterministicScores && !showNucliusReport);
  const showForensicDecisionView = showVantageScorecard && !isCreator && useForensicView;
  const reportId = sessionId || scorecardId || '';
  const fullAnalysisMarkdown = resolveFullAnalysisMarkdown(
    sectionFull,
    hasBlueprintMd ? blueprintMarkdown : undefined
  );
  const showFullAnalysisSection =
    (!showNucliusReport && showVantageScorecard && !!fullAnalysisMarkdown);
  const blueprintFirstLine = hasBlueprintMd ? blueprintMarkdown.trimStart().split('\n')[0]?.trim() ?? '' : '';
  const blueprintOpensWithH1 = blueprintFirstLine.startsWith('# ');
  const canDownload = !!(scorecard || hasBlueprintMd);

  // Deterministic pillars: use V7 scores first, with legacy fallback.
  const pillarValue = (key: 'story_spine' | 'character_strength' | 'theme_strength' | 'commercial_viability') => {
    const v = vs?.[`${key}_pct` as keyof NonNullable<typeof vs>];
    if (typeof v === 'number') return v;
    const top = scorecard?.[`${key}_pct` as keyof Scorecard];
    return typeof top === 'number' ? top : null;
  };
  const fmtSample = (n: number | null | undefined) => {
    if (n == null || Number.isNaN(Number(n))) return '—';
    const x = Number(n);
    const r = Math.round(x * 10) / 10;
    return `${r.toFixed(1)}%`;
  };
  const sub = (key: keyof NonNullable<Scorecard['v7_scores']>, fallback?: number | null) => {
    if (key === 'originality_pct' && scorecard && isOriginalityExcluded(scorecard)) {
      return formatOriginalityDisplay(scorecard);
    }
    return fmtSample(typeof vs?.[key] === 'number' ? vs[key] : fallback);
  };

  // Single title per methodology Step 1 (SCRIPT ANALYSIS RESULT)
  const scorecardTitle = 'SCRIPT ANALYSIS RESULT';
  const labelEtb = isCreator ? 'Story Spine' : 'ETB Structure';
  const labelConceptual = isCreator ? 'Thematic Clarity' : 'Conceptual Relevance';
  const labelEmotional = isCreator ? 'Character Resonance' : 'Emotional Transformation';
  const labelSummary1 = isCreator ? 'Story Spine' : 'Structural Coherence';
  const labelSummary2 = isCreator ? 'Content Pacing' : 'Attention Retention';
  const labelSummary3 = isCreator ? 'Thematic Clarity' : 'Momentum Index';
  const labelSummary4 = isCreator ? 'Character Resonance' : 'Decision Confidence';

  return (
    <IndexWorkspaceShell activeNav="reports">
      <div className="mx-auto max-w-6xl min-w-0 space-y-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
          <div>
            <Button
              onClick={() => navigate('/home')}
              variant="indexOutline"
              className="mb-4"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Index home
            </Button>
            {showVantageScorecard ? (
              <>
                <p className="ni-label mb-2">Nuclius Index report</p>
                <h1 className="text-2xl md:text-3xl font-sans font-bold text-white leading-tight">
                  Forensic scorecard
                </h1>
                <p className="text-white/45 mt-2 font-sans text-sm max-w-lg leading-relaxed">
                  Board-ready Nuclius Index presentation with engagement score, attention resonance,
                  and clearance status. Development mandate fit is assessed separately.
                </p>
              </>
            ) : (
              <>
                <h1 className="text-2xl font-sans font-bold text-white">Script analysis results</h1>
                <p className="text-white/45 mt-2 font-sans text-sm">
                  Nuclius Index engagement scorecard analysis
                </p>
              </>
            )}
          </div>
          {results && canDownload && (
            <div className="shrink-0 flex flex-col gap-2 items-end">
              {showVantageScorecard && !isCreator && (
                <Button
                  variant="indexOutline"
                  onClick={() => setUseForensicView((v) => !v)}
                  className="text-xs"
                >
                  {useForensicView ? 'Pillar detail view' : 'Forensic view'}
                </Button>
              )}
              <Button
                variant="indexPrimary"
                onClick={() => setDownloadDialogOpen(true)}
                className="text-xs"
              >
                <Download className="mr-2 h-4 w-4" />
                {showVantageScorecard ? 'Export report' : 'Download'}
              </Button>
            </div>
          )}
        </div>

        {/* Download format dialog */}
        {results && canDownload && (
          <Dialog open={downloadDialogOpen} onOpenChange={setDownloadDialogOpen}>
            <DialogContent className="sm:max-w-md bg-beige border-border-light text-text-dark">
              <DialogHeader>
                <DialogTitle className="text-text-dark font-serif">Select download format</DialogTitle>
              </DialogHeader>
              {isNucliusBlueprint(results.blueprint as Blueprint | null | undefined) && (
                <label className="flex items-center gap-2 text-sm text-text-dark">
                  <input
                    type="checkbox"
                    checked={includeTechnicalAppendix}
                    onChange={(event) => setIncludeTechnicalAppendix(event.target.checked)}
                    className="h-4 w-4 accent-gold-button"
                  />
                  Include technical appendix (diagnostic agent output)
                </label>
              )}
              <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:gap-2 pt-4">
                <Button
                  onClick={() => {
                    downloadAsText(results, results.script_title ?? undefined, {
                      includeTechnicalAppendix,
                    });
                    setDownloadDialogOpen(false);
                  }}
                  className="bg-gold-button text-white hover:bg-gold-button-dark font-sans w-full sm:w-auto"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download as .txt
                </Button>
                <Button
                  onClick={async () => {
                    try {
                      await downloadAsDocx(results, results.script_title ?? undefined, {
                        includeTechnicalAppendix,
                      });
                      setDownloadDialogOpen(false);
                    } catch (err) {
                      console.error('Download failed:', err);
                    }
                  }}
                  className="bg-gold-button text-white hover:bg-gold-button-dark font-sans w-full sm:w-auto"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download as .docx
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setDownloadDialogOpen(false)}
                  className="font-sans border-border-light text-text-dark hover:bg-beige-dark w-full sm:w-auto"
                >
                  Cancel
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {showNucliusReport && nucliusSections && blueprintObj && (
          <NucliusScorecardReport
            results={results}
            blueprint={blueprintObj}
            sections={nucliusSections}
          />
        )}

        {/* Forensic scorecard — default decision-maker view (legacy) */}
        {!showNucliusReport && showForensicDecisionView && scorecard && (
          <ForensicScorecard
            scorecard={scorecard}
            scriptTitle={results.script_title || 'Untitled screenplay'}
            sessionId={reportId}
            onExport={() =>
              exportForensicScorecardPdf({
                scorecard,
                scriptTitle: results.script_title || 'Untitled screenplay',
                sessionId: reportId,
              })
            }
          />
        )}

        {results?.mandate_overview && !showNucliusReport && (
          <MandateOverviewPanel overview={results.mandate_overview} />
        )}

        {/* V7 scorecard — expandable criterion cards (pillar detail, legacy) */}
        {!showNucliusReport && showVantageScorecard && scorecard && !showForensicDecisionView && (
          <VantageScorecard
            projectName={results.script_title || 'Untitled Script'}
            scorecard={scorecard}
            summaryMarkdown={sectionSummary}
            scorecardMarkdown={sectionScorecard || (hasBlueprintMd ? blueprintMarkdown : '')}
            fullMarkdown={sectionFull || (hasBlueprintMd ? blueprintMarkdown : '')}
            genre={scorecard.main_genre}
            logline={scorecard.logline}
          />
        )}

        {/* Legacy scorecard display (non-deterministic or fallback) */}
        {scorecard && !showVantageScorecard && !showCreatorBlueprintOnly && !showNucliusReport && (
          <div className="bg-beige-dark rounded-sm border border-border-light p-8 shadow-sm">
            <div className="space-y-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-sm font-sans font-light text-text-dark opacity-70 uppercase tracking-wider mb-2">
                    {scorecardTitle}
                  </h2>
                  <h3 className="text-2xl font-serif font-bold text-text-dark">
                    {results.script_title || 'Script Analysis'}
                  </h3>
                </div>
                {scorecard.main_genre && (
                  <span className="text-sm font-sans font-medium text-text-dark opacity-80 shrink-0">
                    {scorecard.main_genre}
                  </span>
                )}
              </div>
              {scorecard.logline && (
                <p className="text-sm font-sans text-text-dark opacity-90 italic">
                  {scorecard.logline}
                </p>
              )}
              <div className="space-y-4">
                <div>
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-4xl font-serif font-bold text-text-dark">
                      {Math.round(scorecard.overall_engagement_score)}
                    </span>
                    <span className="text-xl font-serif text-text-dark opacity-60">/100</span>
                  </div>
                  <p className="text-sm font-sans text-text-dark opacity-70">
                    {isCreator ? 'ENGAGEMENT SCORE' : 'OVERALL ENGAGEMENT SCORE'}
                  </p>
                  {isCreator && (
                    <p className="text-xs font-sans text-text-dark opacity-60 italic mt-2">
                      Percentages are calculated using Nuclius&apos;s proprietary methodology, optimised for measuring overall audience impact.
                    </p>
                  )}
                </div>
                
                <div className="space-y-3 pt-4 border-t border-border-light">
                  {useDeterministicScores ? (
                    <>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-sans font-semibold text-text-dark">Story Spine</span>
                          <span className="text-sm font-sans font-medium text-text-dark">{fmtSample(pillarValue('story_spine'))}</span>
                        </div>
                        <div className="pl-3 space-y-1 text-sm font-sans text-text-dark opacity-90">
                          <div className="flex justify-between"><span>Story Strength</span><span>{sub('etb_structure_pct')}</span></div>
                          <div className="flex justify-between"><span>Pacing</span><span>{sub('pacing_pct')}</span></div>
                          <div className="flex justify-between"><span>Story Beat Strength</span><span>{sub('story_beat_strength_pct')}</span></div>
                          <div className="flex justify-between"><span>Scene Strength %</span><span>{sub('scene_strength_pct', scorecard?.scene_strength)}</span></div>
                          <div className="flex justify-between"><span>Concentration of Overarching Problem</span><span>{sub('concentration_pct')}</span></div>
                        </div>
                      </div>
                      <div className="space-y-2 pt-2 border-t border-border-light">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-sans font-semibold text-text-dark">Character Strength</span>
                          <span className="text-sm font-sans font-medium text-text-dark">{fmtSample(pillarValue('character_strength'))}</span>
                        </div>
                        <div className="pl-3 space-y-1 text-sm font-sans text-text-dark opacity-90">
                          <div className="flex justify-between"><span>Character Concentration</span><span>{sub('character_concentration_pct')}</span></div>
                          <div className="flex justify-between"><span>Character Resonance</span><span>{sub('character_resonance_pct')}</span></div>
                          <div className="flex justify-between"><span>Character Active Metre</span><span>{sub('character_active_pct')}</span></div>
                          <div className="flex justify-between"><span>Dialogue Strength</span><span>{sub('dialogue_strength_pct', scorecard?.dialogue_strength)}</span></div>
                          <div className="flex justify-between"><span>Character Journey</span><span>{sub('character_journey_pct')}</span></div>
                        </div>
                      </div>
                      <div className="space-y-2 pt-2 border-t border-border-light">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-sans font-semibold text-text-dark">Theme Strength</span>
                          <span className="text-sm font-sans font-medium text-text-dark">{fmtSample(pillarValue('theme_strength'))}</span>
                        </div>
                        <div className="pl-3 space-y-1 text-sm font-sans text-text-dark opacity-90">
                          <div className="flex justify-between"><span>Thematic Quality</span><span>{sub('thematic_quality_pct')}</span></div>
                          <div className="flex justify-between"><span>Authenticity Score</span><span>{sub('authenticity_pct')}</span></div>
                          <div className="flex justify-between"><span>Genre Promise</span><span>{sub('genre_promise_pct')}</span></div>
                        </div>
                      </div>
                      <div className="space-y-2 pt-2 border-t border-border-light">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-sans font-semibold text-text-dark">Commercial Viability</span>
                          <span className="text-sm font-sans font-medium text-text-dark">{fmtSample(pillarValue('commercial_viability'))}</span>
                        </div>
                        <div className="pl-3 space-y-1 text-sm font-sans text-text-dark opacity-90">
                          <div className="flex justify-between"><span>Originality Metre</span><span>{sub('originality_pct')}</span></div>
                          <div className="flex justify-between"><span>Target Market</span><span>{sub('target_market_pct')}</span></div>
                        </div>
                      </div>
                    </>
                  ) : isCreator ? (
                    <>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-sans text-text-dark">Story Spine</span>
                        <span className="text-sm font-sans font-medium text-text-dark">
                          {Math.round((scorecard.etb_structure?.raw_score ?? 0) * 10)}%
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-sans text-text-dark">Content Pacing</span>
                        <span className="text-sm font-sans font-medium text-text-dark">
                          {scorecard.genre_affect ? Math.round((scorecard.genre_affect.raw_score ?? 0) * 10) : scorecard.engagement_percentage?.toFixed?.(0) ?? '0'}%
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-sans text-text-dark">General Thematic Clarity</span>
                        <span className="text-sm font-sans font-medium text-text-dark">
                          {Math.round((scorecard.conceptual_relevance?.raw_score ?? 0) * 10)}%
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-sans text-text-dark">Authenticity Score</span>
                        <span className="text-sm font-sans font-medium text-text-dark">
                          {Math.round((scorecard.conceptual_relevance?.raw_score ?? 0) * 10)}%
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-sans text-text-dark">Character Resonance</span>
                        <span className="text-sm font-sans font-medium text-text-dark">
                          {Math.round((scorecard.emotional_transformation?.raw_score ?? 0) * 10)}%
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-sans text-text-dark">Genre Accuracy</span>
                        <span className="text-sm font-sans font-medium text-text-dark">
                          {scorecard.genre_affect ? Math.round((scorecard.genre_affect.raw_score ?? 0) * 10) : scorecard.engagement_percentage?.toFixed?.(0) ?? '0'}%
                        </span>
                      </div>
                      {typeof scorecard.scene_strength === 'number' && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-sans text-text-dark">Scene Strength</span>
                          <span className="text-sm font-sans font-medium text-text-dark">
                            {Math.round(scorecard.scene_strength)}%
                          </span>
                        </div>
                      )}
                      {typeof scorecard.dialogue_strength === 'number' && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-sans text-text-dark">Dialogue</span>
                          <span className="text-sm font-sans font-medium text-text-dark">
                            {Math.round(scorecard.dialogue_strength)}%
                          </span>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-sans text-text-dark">{labelSummary1}</span>
                        <span className="text-sm font-sans font-medium text-text-dark">
                          {scorecard.etb_structure?.raw_score >= 8 ? 'High' : 
                           scorecard.etb_structure?.raw_score >= 6 ? 'Medium' : 'Low'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-sans text-text-dark">{labelSummary2}</span>
                        <span className="text-sm font-sans font-medium text-text-dark">
                          {scorecard.engagement_percentage?.toFixed?.(0) ?? '0'}%
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-sans text-text-dark">{labelSummary3}</span>
                        <span className="text-sm font-sans font-medium text-text-dark">
                          {scorecard.engagement_percentage != null && scorecard.engagement_percentage >= 70 ? 'Strong' : 
                           scorecard.engagement_percentage != null && scorecard.engagement_percentage >= 50 ? 'Moderate' : 'Weak'}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-sans text-text-dark">{labelSummary4}</span>
                        <span className="text-sm font-sans font-medium text-text-dark">
                          {scorecard.engagement_percentage != null && scorecard.engagement_percentage >= 70 ? 'Viable' : 
                           scorecard.engagement_percentage != null && scorecard.engagement_percentage >= 50 ? 'Marginal' : 'Risky'}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
              
              <p className="text-xs font-sans text-text-dark opacity-50 pt-4 border-t border-border-light">
                Generated by Nuclius Index
              </p>
            </div>
          </div>
        )}

        {/* Scorecard Breakdown (legacy layout only) */}
        {scorecard && !showVantageScorecard && !showCreatorBlueprintOnly && (() => {
          if (useDeterministicScores) {
            const pillars: { label: string; raw: number | null }[] = [
              { label: 'Story Spine', raw: pillarValue('story_spine') },
              { label: 'Character Strength', raw: pillarValue('character_strength') },
              { label: 'Theme Strength', raw: pillarValue('theme_strength') },
              { label: 'Commercial Viability', raw: pillarValue('commercial_viability') },
            ];
            return (
              <div className="bg-beige-dark rounded-sm border border-border-light p-6">
                <h2 className="text-2xl font-serif font-semibold mb-6 text-text-dark">Scorecard Breakdown</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {pillars.map(({ label, raw }) => (
                    <div key={label} className="space-y-2">
                      <span className="text-sm font-medium text-text-dark font-sans">{label}</span>
                      <div className="h-2 bg-border-light rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gold-button transition-all"
                          style={{ width: `${raw != null ? Math.min(100, Math.round(raw)) : 0}%` }}
                        />
                      </div>
                      <span className="text-xs font-sans text-text-dark opacity-70">{fmtSample(raw)}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          }
          const weights = scorecard.score_weightings;
          const breakdown = scorecard.weighted_breakdown;
          const compScores = scorecard.component_scores;
          const components = [
            { key: 'story_spine', label: isCreator ? 'Story Spine' : 'ETB Structure', raw: compScores?.story_spine ?? scorecard.etb_structure?.raw_score, max: 10 },
            { key: 'content_pacing', label: 'Content Pacing', raw: compScores?.content_pacing, max: 10 },
            { key: 'general_thematic_clarity', label: isCreator ? 'General Thematic Clarity' : 'Thematic Clarity', raw: compScores?.general_thematic_clarity ?? scorecard.conceptual_relevance?.raw_score, max: 10 },
            { key: 'authenticity', label: 'Authenticity', raw: compScores?.authenticity, max: 10 },
            { key: 'character_resonance', label: isCreator ? 'Character Resonance' : 'Emotional Transformation', raw: compScores?.character_resonance ?? scorecard.emotional_transformation?.raw_score, max: 10 },
            { key: 'genre_accuracy', label: isCreator ? 'Genre Accuracy' : 'Genre', raw: compScores?.genre_accuracy ?? scorecard.genre_affect?.raw_score, max: 10 },
            { key: 'scene_strength', label: 'Scene Strength', raw: compScores?.scene_strength ?? scorecard.scene_strength, max: 100 },
            { key: 'dialogue', label: 'Dialogue', raw: compScores?.dialogue ?? scorecard.dialogue_strength, max: 100 },
          ];
          const hasWeightedBreakdown = breakdown && Object.keys(breakdown).length > 0;
          return (
            <div className="bg-beige-dark rounded-sm border border-border-light p-6">
              <h2 className="text-2xl font-serif font-semibold mb-6 text-text-dark">Scorecard Breakdown</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {hasWeightedBreakdown && weights
                  ? components
                      .filter((c) => weights[c.key] > 0)
                      .map((c) => (
                          <div key={c.key} className="space-y-2">
                            <span className="text-sm font-medium text-text-dark font-sans">{c.label}</span>
                            <div className="h-2 bg-border-light rounded-full overflow-hidden">
                              <div
                                className="h-full bg-gold-button transition-all"
                                style={{ width: `${c.raw != null ? (c.max === 100 ? c.raw : (c.raw / 10) * 100) : 0}%` }}
                              />
                            </div>
                          </div>
                        ))
                  : (
                    <>
                      <div className="space-y-2">
                        <span className="text-sm font-medium text-text-dark font-sans">{labelEtb}</span>
                        <div className="h-2 bg-border-light rounded-full overflow-hidden">
                          <div className="h-full bg-gold-button transition-all" style={{ width: `${((scorecard.etb_structure?.raw_score ?? 0) / 10) * 100}%` }} />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <span className="text-sm font-medium text-text-dark font-sans">{labelConceptual}</span>
                        <div className="h-2 bg-border-light rounded-full overflow-hidden">
                          <div className="h-full bg-gold-button transition-all" style={{ width: `${((scorecard.conceptual_relevance?.raw_score ?? 0) / 10) * 100}%` }} />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <span className="text-sm font-medium text-text-dark font-sans">{labelEmotional}</span>
                        <div className="h-2 bg-border-light rounded-full overflow-hidden">
                          <div className="h-full bg-gold-button transition-all" style={{ width: `${((scorecard.emotional_transformation?.raw_score ?? 0) / 10) * 100}%` }} />
                        </div>
                      </div>
                    </>
                  )}
              </div>
            </div>
          );
        })()}

        {/* V7 full analysis output — all audiences with deterministic scorecard */}
        {showFullAnalysisSection && (
          <div className="space-y-4">
            <CollapsibleSection
              title="Full analysis output"
              description="The complete output of each Nuclius Index agent."
              variant="gold"
            >
              <div className="[&_.blueprint-markdown]:text-white/80 [&_h1]:text-white [&_h2]:text-white [&_strong]:text-white [&_pre]:bg-black [&_pre]:border-[#2a2a2a]">
                <BlueprintMarkdown markdown={fullAnalysisMarkdown} />
              </div>
            </CollapsibleSection>
          </div>
        )}

        {/* V7 blueprint — legacy three-section layout (older scorecards without deterministic UI) */}
        {showThreeSection && !showVantageScorecard && (
          <div className="space-y-4">
            <CollapsibleSection title="Scorecard" defaultOpen description="All scores: overall, pillars and sub-metrics.">
              {sectionScorecard ? (
                <BlueprintMarkdown markdown={sectionScorecard} />
              ) : (
                <p className="text-sm font-sans text-text-dark opacity-70">No scorecard available.</p>
              )}
            </CollapsibleSection>

            <CollapsibleSection
              title="Summary of insights and actions"
              description="What works, what does not, and actionable notes for each metric."
            >
              {sectionSummary ? (
                <BlueprintMarkdown markdown={sectionSummary} />
              ) : (
                <p className="text-sm font-sans text-text-dark opacity-70">No summary available.</p>
              )}
            </CollapsibleSection>

            <CollapsibleSection
              title="Full analysis output"
              description="The complete output of each Nuclius Index agent."
            >
              {sectionFull ? (
                <BlueprintMarkdown markdown={sectionFull} />
              ) : (
                <p className="text-sm font-sans text-text-dark opacity-70">No detailed agent output available.</p>
              )}
            </CollapsibleSection>

            <p className="text-xs font-sans text-text-dark opacity-50 px-2">Generated by Nuclius Index</p>
          </div>
        )}

        {/* V7 canonical blueprint (markdown) — fallback when sections are absent (older scorecards) */}
        {isCreator && hasBlueprintMd && !showThreeSection && (
          <div className="bg-beige-dark rounded-sm border border-border-light p-6 md:p-8 shadow-sm">
            {!blueprintOpensWithH1 && (
              <h2 className="text-2xl font-serif font-semibold mb-4 text-text-dark">Analysis blueprint</h2>
            )}
            <BlueprintMarkdown markdown={blueprintMarkdown!} />
            <p className="text-xs font-sans text-text-dark opacity-50 pt-6 mt-6 border-t border-border-light">
              Generated by Nuclius Index
            </p>
          </div>
        )}

        {/* Improvement Blueprint (Creator only, legacy pitfalls/action steps — hidden when V7 markdown is primary) */}
        {isCreator &&
          !showCreatorBlueprintOnly &&
          results.blueprint &&
          ((results.blueprint.identified_pitfalls?.length ?? 0) > 0 ||
            (results.blueprint.action_steps?.length ?? 0) > 0) && (
          <div className="bg-beige-dark rounded-sm border border-border-light p-6">
            <h2 className="text-2xl font-serif font-semibold mb-6 text-text-dark">
              Improvement Blueprint
            </h2>
            <p className="text-sm text-text-dark opacity-80 font-sans mb-4">
              We provide full context for our analysis, along with clear, actionable suggestions to improve your engagement score.
            </p>
            {results.blueprint.identified_pitfalls && results.blueprint.identified_pitfalls.length > 0 && (
              <div className="space-y-4 mb-6">
                <h3 className="text-lg font-serif font-semibold text-text-dark">Identified Pitfalls</h3>
                <ul className="space-y-3">
                  {results.blueprint.identified_pitfalls.map((p, i) => (
                    <li key={i} className="text-sm font-sans text-text-dark border-l-2 border-gold-button pl-4 py-1">
                      <span className="font-medium">{p.category}</span>
                      {p.severity && (
                        <span className={cn(
                          'ml-2 text-xs font-medium',
                          p.severity === 'Critical' ? 'text-red-600' : p.severity === 'Warning' ? 'text-amber-600' : 'text-text-dark opacity-70'
                        )}>
                          {p.severity}
                        </span>
                      )}
                      <p className="mt-1 text-text-dark">{p.issue}</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {results.blueprint.action_steps && results.blueprint.action_steps.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-lg font-serif font-semibold text-text-dark">Action Steps</h3>
                <ol className="list-decimal list-inside space-y-2 text-sm font-sans text-text-dark">
                  {results.blueprint.action_steps.map((step, i) => (
                    <li key={i} className="pl-2">{step}</li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        )}

        {/* Commission Summary (Decision Maker only) */}
        {!isCreator && results.blueprint?.commission_summary && (
          <div className="bg-beige-dark rounded-sm border border-border-light p-6">
            <h2 className="text-2xl font-serif font-semibold mb-6 text-text-dark">Commission Summary</h2>
            <p className="text-sm font-sans text-text-dark whitespace-pre-wrap leading-relaxed">
              {results.blueprint.commission_summary}
            </p>
          </div>
        )}

        {/* Detailed Analysis (hidden when V7 blueprint markdown is the primary surface) */}
        {scorecard && !blueprintMarkdown && (() => {
          // Extract agent analysis text from various possible locations
          const state = results as any;
          const etbAnalysis = (state.etb_analysis ?? results.etb_analysis) ||
            (results.agent_findings?.etb_agent?.findings) ||
            (results.interaction_history?.find((e: any) => e.agent === 'etb_agent')?.response);
          const conceptualAnalysis = (state.conceptual_analysis ?? results.conceptual_analysis) ||
            (results.agent_findings?.conceptual_agent?.findings) ||
            (results.interaction_history?.find((e: any) => e.agent === 'conceptual_agent')?.response);
          const emotionalAnalysis = (state.emotional_analysis ?? results.emotional_analysis) ||
            (results.agent_findings?.emotional_agent?.findings) ||
            (results.interaction_history?.find((e: any) => e.agent === 'emotional_agent')?.response);
          const genreAnalysis = (state.genre_analysis ?? results.genre_analysis) ||
            (results.agent_findings?.engage_agent?.findings) ||
            (results.interaction_history?.find((e: any) => e.agent === 'engage_agent')?.response);
          const sceneAnalysis = (state.scene_analysis ?? results.scene_analysis) ||
            (results.agent_findings?.scene_agent?.findings) ||
            (results.interaction_history?.find((e: any) => e.agent === 'scene_agent')?.response);
          const dialogueAnalysis = (state.dialogue_analysis ?? results.dialogue_analysis) ||
            (results.agent_findings?.dialogue_agent?.findings) ||
            (results.interaction_history?.find((e: any) => e.agent === 'dialogue_agent')?.response);

          // Creator: render raw agent text with newlines and **bold** preserved
          const sanitizeAgentText = (text: string | undefined): string => {
            if (!text || typeof text !== 'string') return '';
            const trimmed = text.trim();
            const looksJson =
              trimmed.startsWith('{') ||
              trimmed.startsWith('[') ||
              /```json/i.test(trimmed) ||
              /"\w+"\s*:\s*/.test(trimmed);
            if (looksJson) {
              return 'Analysis output required normalization; readable narrative was not available for this section.';
            }
            return trimmed;
          };

          const renderAgentText = (text: string | undefined): React.ReactNode => {
            const safeText = sanitizeAgentText(text);
            if (!safeText) return <span className="text-text-dark opacity-60">Analysis not available.</span>;
            return safeText.split('\n').map((line, i) => {
              const trimmed = line.trim();
              if (trimmed === '---') {
                return <hr key={i} className="my-4 border-border-light" />;
              }
              const parts: React.ReactNode[] = [];
              let remaining = line;
              let key = 0;
              while (remaining.length > 0) {
                const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
                if (boldMatch && boldMatch.index !== undefined) {
                  if (boldMatch.index > 0) parts.push(<span key={key++}>{remaining.slice(0, boldMatch.index)}</span>);
                  parts.push(<strong key={key++} className="font-semibold text-text-dark">{boldMatch[1]}</strong>);
                  remaining = remaining.slice(boldMatch.index + boldMatch[0].length);
                } else {
                  parts.push(<span key={key++}>{remaining}</span>);
                  break;
                }
              }
              return <p key={i} className="mb-2 last:mb-0 text-sm leading-relaxed text-text-dark">{parts}</p>;
            });
          };

          const splitGenreAnalysis = (): { contentPacing: string; genreAccuracy: string } => {
            const raw = typeof genreAnalysis === 'string' ? genreAnalysis : '';
            const pacingIdx = raw.search(/\*\*CONTENT PACING\*\*/i);
            const genreIdx = raw.search(/\*\*GENRE ACCURACY\*\*/i);
            if (pacingIdx !== -1 && genreIdx !== -1 && genreIdx > pacingIdx) {
              return {
                contentPacing: raw.slice(pacingIdx, genreIdx).replace(/^\*\*CONTENT PACING\*\*\s*/i, '').trim(),
                genreAccuracy: raw.slice(genreIdx).replace(/^\*\*GENRE ACCURACY\*\*\s*/i, '').trim(),
              };
            }
            if (pacingIdx !== -1) {
              const after = raw.slice(pacingIdx).replace(/^\*\*CONTENT PACING\*\*\s*/i, '').trim();
              return { contentPacing: after, genreAccuracy: raw.slice(0, pacingIdx).trim() || after };
            }
            if (genreIdx !== -1) {
              const after = raw.slice(genreIdx).replace(/^\*\*GENRE ACCURACY\*\*\s*/i, '').trim();
              return { contentPacing: raw.slice(0, genreIdx).trim() || after, genreAccuracy: after };
            }
            return { contentPacing: raw, genreAccuracy: raw };
          };

          if (isCreator) {
            const { contentPacing, genreAccuracy } = splitGenreAnalysis();
            const encouragingQuote = results.blueprint?.encouraging_quote?.trim() || 'Good writing is essentially rewriting. — Roald Dahl';
            return (
              <div className="bg-beige-dark rounded-sm border border-border-light p-6">
                <h2 className="text-2xl font-serif font-semibold mb-4 text-text-dark">Detailed Analysis</h2>
                <p className="text-sm font-sans text-text-dark opacity-90 mb-6">
                  We provide full context for our analysis, along with clear, actionable suggestions to improve your engagement score.
                </p>
                <div className="space-y-6 prose prose-sm max-w-none text-text-dark font-sans">
                  <section>
                    <h3 className="text-lg font-serif font-semibold text-text-dark mb-2">STORY SPINE</h3>
                    <div className="text-sm leading-relaxed">{renderAgentText(etbAnalysis)}</div>
                  </section>
                  <section>
                    <h3 className="text-lg font-serif font-semibold text-text-dark mb-2">CONTENT PACING</h3>
                    <div className="text-sm leading-relaxed">{renderAgentText(contentPacing || genreAnalysis)}</div>
                  </section>
                  <section>
                    <h3 className="text-lg font-serif font-semibold text-text-dark mb-2">THEMATIC CLARITY</h3>
                    <div className="text-sm leading-relaxed">{renderAgentText(conceptualAnalysis)}</div>
                  </section>
                  <section>
                    <h3 className="text-lg font-serif font-semibold text-text-dark mb-2">CHARACTER RESONANCE</h3>
                    <div className="text-sm leading-relaxed">{renderAgentText(emotionalAnalysis)}</div>
                  </section>
                  <section>
                    <h3 className="text-lg font-serif font-semibold text-text-dark mb-2">GENRE ACCURACY</h3>
                    <div className="text-sm leading-relaxed">{renderAgentText(genreAccuracy || genreAnalysis)}</div>
                  </section>
                  {sceneAnalysis && (
                    <section>
                      <h3 className="text-lg font-serif font-semibold text-text-dark mb-2">SCENE STRENGTH</h3>
                      <div className="text-sm leading-relaxed">{renderAgentText(sceneAnalysis)}</div>
                    </section>
                  )}
                  {dialogueAnalysis && (
                    <section>
                      <h3 className="text-lg font-serif font-semibold text-text-dark mb-2">DIALOGUE</h3>
                      <div className="text-sm leading-relaxed">{renderAgentText(dialogueAnalysis)}</div>
                    </section>
                  )}
                  <p className="text-sm font-sans italic text-text-dark opacity-90 pt-4 border-t border-border-light">
                    {encouragingQuote}
                  </p>
                </div>
              </div>
            );
          }

          // Helper function to extract key insights from analysis text (actor-focused, no improvement suggestions)
          const extractInsights = (analysisText: string | undefined, score: number, componentName: string): string[] => {
            if (!analysisText || typeof analysisText !== 'string') return [];
            
            const insights: string[] = [];
            
            // Extract descriptive sentences that explain the current state (not improvements)
            // Split into sentences and filter for descriptive content
            const sentences = analysisText
              .split(/[.!?]\s+/)
              .map(s => s.trim())
              .filter(s => s.length > 30 && s.length < 400);
            
            // Filter out improvement-focused language
            const exclusionWords = ['improve', 'should', 'recommend', 'fix', 'enhance', 'strengthen', 'address', 'consider', 'suggest', 'action', 'step'];
            
            for (const sentence of sentences) {
              const lowerSentence = sentence.toLowerCase();
              const hasExclusion = exclusionWords.some(word => lowerSentence.includes(word));
              
              if (!hasExclusion) {
                // Look for descriptive patterns about the script's current state
                const descriptivePatterns = [
                  /(?:demonstrates|shows|exhibits|has|contains|features|displays|presents|reveals|indicates|suggests|reflects)/i,
                  /(?:lacks|misses|fails to|does not|doesn't|absent|missing)/i,
                  /(?:strong|weak|moderate|limited|extensive|comprehensive|clear|unclear|consistent|inconsistent)/i,
                ];
                
                if (descriptivePatterns.some(pattern => pattern.test(sentence))) {
                  insights.push(sentence);
                  if (insights.length >= 2) break; // Limit to 2 insights per component
                }
              }
            }
            
            return insights;
          };

          // Build unified narrative focused on actor decision-making
          const buildNarrative = (): string => {
            const overallScore = scorecard.overall_engagement_score;
            const engagementPercent = scorecard.engagement_percentage;
            
            let narrative = `This script achieves an overall engagement score of ${overallScore}/100, representing ${engagementPercent}% engagement potential. `;
            
            // Overall assessment from actor perspective
            if (engagementPercent >= 80) {
              narrative += `This indicates a highly viable project with strong narrative foundations and significant audience engagement potential. `;
            } else if (engagementPercent >= 60) {
              narrative += `This suggests a viable project with solid structural elements and good engagement prospects. `;
            } else if (engagementPercent >= 40) {
              narrative += `This indicates a project with developing potential, though some structural elements show variability. `;
            } else {
              narrative += `This suggests a project with limited engagement potential, indicating significant structural challenges. `;
            }

            // ETB Structure insights - focus on what it means for narrative quality
            const etbScore = scorecard.etb_structure.raw_score;
            const etbInsights = extractInsights(etbAnalysis, etbScore, 'ETB Structure');
            if (etbInsights.length > 0) {
              // Use the first insight, making it flow naturally
              const insight = etbInsights[0].charAt(0).toLowerCase() + etbInsights[0].slice(1);
              narrative += `\n\nThe narrative structure (${etbScore.toFixed(1)}/10) ${insight}`;
            } else {
              if (etbScore >= 8) {
                narrative += `\n\nThe narrative structure demonstrates strong structural coherence, with well-defined story beats that drive the narrative forward effectively. This indicates a script with clear pacing and logical progression, which supports consistent audience engagement throughout.`;
              } else if (etbScore >= 6) {
                narrative += `\n\nThe narrative structure shows moderate coherence, with most key story beats present. Some structural gaps exist that may affect pacing and narrative momentum, potentially impacting how consistently the story maintains audience attention.`;
              } else {
                narrative += `\n\nThe narrative structure shows limited coherence, with missing or underdeveloped story beats. This may result in uneven pacing and narrative flow, which could affect the script's ability to sustain audience engagement.`;
              }
            }

            // Conceptual Relevance insights - focus on thematic clarity and audience appeal
            const conceptualScore = scorecard.conceptual_relevance.raw_score;
            const conceptualInsights = extractInsights(conceptualAnalysis, conceptualScore, 'Conceptual Relevance');
            if (conceptualInsights.length > 0) {
              const insight = conceptualInsights[0].charAt(0).toLowerCase() + conceptualInsights[0].slice(1);
              narrative += ` The thematic foundation (${conceptualScore.toFixed(1)}/10) ${insight}`;
            } else {
              if (conceptualScore >= 8) {
                narrative += ` The thematic foundation demonstrates strong conceptual clarity and cultural specificity, providing a clear and engaging central theme. This suggests the script has a well-defined message that can resonate with target audiences.`;
              } else if (conceptualScore >= 6) {
                narrative += ` The thematic foundation shows moderate clarity, with a discernible central theme. Some conceptual elements may be less defined, which could affect the script's ability to connect with specific audience segments.`;
              } else {
                narrative += ` The thematic foundation shows limited clarity, with an unclear or underdeveloped central theme. This may affect the script's ability to establish a strong connection with audiences and could impact its market positioning.`;
              }
            }

            // Emotional Transformation insights - focus on character opportunities for actors
            const emotionalScore = scorecard.emotional_transformation.raw_score;
            const emotionalInsights = extractInsights(emotionalAnalysis, emotionalScore, 'Emotional Transformation');
            if (emotionalInsights.length > 0) {
              const insight = emotionalInsights[0].charAt(0).toLowerCase() + emotionalInsights[0].slice(1);
              narrative += `\n\nCharacter development (${emotionalScore.toFixed(1)}/10) ${insight}`;
            } else {
              if (emotionalScore >= 8) {
                narrative += `\n\nCharacter development demonstrates strong emotional transformation, with clear character arcs that provide meaningful opportunities for performance. Characters show significant growth and evolution, offering actors substantial material to work with.`;
              } else if (emotionalScore >= 6) {
                narrative += `\n\nCharacter development shows moderate transformation, with some character evolution present. Emotional depth may vary throughout the narrative, which could provide some performance opportunities while limiting others.`;
              } else {
                narrative += `\n\nCharacter development shows limited transformation, with minimal character evolution. This may limit the range of performance opportunities available and could affect the depth of character portrayal.`;
              }
            }

            // Genre/Affect insights - focus on market positioning and audience expectations
            if (scorecard.genre_affect) {
              const genreScore = scorecard.genre_affect.raw_score;
              const genreInsights = extractInsights(genreAnalysis, genreScore, 'Genre Consistency');
              if (genreInsights.length > 0) {
                const insight = genreInsights[0].charAt(0).toLowerCase() + genreInsights[0].slice(1);
                narrative += ` Genre consistency (${genreScore.toFixed(1)}/10) ${insight}`;
              } else {
                if (genreScore >= 8) {
                  narrative += ` Genre consistency is strong, with the script maintaining its emotional promise throughout. This supports clear market positioning and helps audiences understand what to expect from the project.`;
                } else if (genreScore >= 6) {
                  narrative += ` Genre consistency is moderate, with the script generally maintaining its emotional tone. Some gaps exist that may affect how consistently the project delivers on its genre expectations.`;
                } else {
                  narrative += ` Genre consistency is limited, with inconsistent emotional delivery. This may affect how clearly the project positions itself in the market and could impact audience expectations.`;
                }
              }
            }

            // Project viability conclusion - actor-focused
            narrative += `\n\nFrom a project viability perspective, `;
            if (engagementPercent >= 70) {
              narrative += `this script demonstrates strong potential for audience engagement and market success. The structural and thematic foundations support a compelling narrative that offers meaningful character development opportunities. This suggests a project with good prospects for both critical and commercial reception.`;
            } else if (engagementPercent >= 50) {
              narrative += `this script shows developing potential with some strong elements. Certain structural or thematic aspects may impact overall engagement, which could affect the project's market performance and audience reception.`;
            } else {
              narrative += `this script faces significant challenges in structural coherence, thematic clarity, or character development. These factors may affect the project's viability, audience engagement, and potential for market success.`;
            }

            return narrative;
          };

          const narrative = buildNarrative();

          // Determine if script is positive or negative based on engagement percentage
          // Positive: >= 50% (Marginal or Viable), Negative: < 50% (Risky)
          const isPositiveScript = scorecard.engagement_percentage >= 50;

          // Function to highlight key phrases that actors would be interested in
          const highlightActorOpportunities = (text: string): React.ReactNode => {
            // Positive phrases - opportunities for good scripts
            const positivePhrases = [
              'critical and commercial reception',
              'critical and commercial success',
              'character development opportunities',
              'range of performance opportunities',
              'meaningful character development',
              'significant audience engagement',
              'consistent audience engagement',
              'strong narrative foundations',
              'well-defined story beats',
              'clear character arcs',
              'strong emotional transformation',
              'can resonate with target audiences',
              'depth of character portrayal',
              'substantial material to work with',
              'performance opportunities',
              'meaningful opportunities',
              'audience engagement',
              'market success',
              'highly viable project',
              'strong potential',
              'good prospects',
              'character arcs',
              'emotional transformation',
              'growth and evolution',
              'clear market positioning',
              'audience reception',
              'viable project',
              'engagement potential',
              'compelling narrative',
              'logical progression',
              'well-defined message',
              'significant growth',
              'character evolution',
              'emotional depth',
              'clear pacing',
              'strong structural coherence',
              'solid structural elements',
              'good engagement prospects',
              'developing potential',
              'meaningful character development opportunities',
            ];

            // Negative phrases - warnings for poor scripts
            const negativePhrases = [
              'limited engagement potential',
              'significant structural challenges',
              'structural gaps',
              'uneven pacing',
              'narrative flow',
              'sustain audience engagement',
              'limited clarity',
              'unclear or underdeveloped',
              'affect the script\'s ability',
              'establish a strong connection',
              'impact its market positioning',
              'limited transformation',
              'minimal character evolution',
              'limit the range of performance opportunities',
              'affect the depth of character portrayal',
              'inconsistent emotional delivery',
              'affect how clearly the project positions itself',
              'impact audience expectations',
              'faces significant challenges',
              'affect the project\'s viability',
              'potential for market success',
              'affect the project\'s market performance',
              'affect audience reception',
              'limited coherence',
              'missing or underdeveloped',
              'may affect',
              'could affect',
              'may impact',
              'could impact',
              'may result in',
              'could result in',
              'structural challenges',
              'thematic clarity',
              'character development',
              'project\'s viability',
              'audience engagement',
              'market success',
              'significant challenges',
              'developing potential',
              'some structural elements show variability',
              'certain structural or thematic aspects may impact',
            ];

            // Use appropriate phrase list based on script quality
            const phrasesToHighlight = isPositiveScript ? positivePhrases : negativePhrases;

            // Process text to find and highlight all matching phrases
            const parts: Array<{ text: string; highlight: boolean; index: number }> = [];
            const matches: Array<{ start: number; end: number; phrase: string }> = [];

            // Find all matches (case-insensitive)
            phrasesToHighlight.forEach(phrase => {
              const regex = new RegExp(phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
              let match;
              while ((match = regex.exec(text)) !== null) {
                matches.push({
                  start: match.index,
                  end: match.index + match[0].length,
                  phrase: match[0],
                });
              }
            });

            // Sort matches by start position
            matches.sort((a, b) => a.start - b.start);

            // Remove overlapping matches (keep longer ones)
            const nonOverlapping: typeof matches = [];
            for (const match of matches) {
              const overlaps = nonOverlapping.some(
                existing => !(match.end <= existing.start || match.start >= existing.end)
              );
              if (!overlaps) {
                nonOverlapping.push(match);
              } else {
                // If overlapping, keep the longer one
                const existingIndex = nonOverlapping.findIndex(
                  existing => !(match.end <= existing.start || match.start >= existing.end)
                );
                if (existingIndex !== -1) {
                  const existing = nonOverlapping[existingIndex];
                  if (match.end - match.start > existing.end - existing.start) {
                    nonOverlapping[existingIndex] = match;
                  }
                }
              }
            }

            // Build parts array
            let lastIndex = 0;
            nonOverlapping.forEach((match, idx) => {
              // Add text before match
              if (match.start > lastIndex) {
                parts.push({
                  text: text.substring(lastIndex, match.start),
                  highlight: false,
                  index: lastIndex,
                });
              }

              // Add highlighted match
              parts.push({
                text: match.phrase,
                highlight: true,
                index: match.start,
              });

              lastIndex = match.end;
            });

            // Add remaining text
            if (lastIndex < text.length) {
              parts.push({
                text: text.substring(lastIndex),
                highlight: false,
                index: lastIndex,
              });
            }

            // Render parts
            return (
              <>
                {parts.map((part, idx) =>
                  part.highlight ? (
                    <span
                      key={`highlight-${part.index}-${idx}`}
                      className="font-bold text-gold-button"
                    >
                      {part.text}
                    </span>
                  ) : (
                    <span key={`text-${part.index}-${idx}`}>{part.text}</span>
                  )
                )}
              </>
            );
          };
                  
                  return (
            <div className="bg-beige-dark rounded-sm border border-border-light p-6">
              <h2 className="text-2xl font-serif font-semibold mb-6 text-text-dark">Detailed Analysis</h2>
              <div className="prose prose-sm max-w-none text-text-dark font-sans">
                <div className="whitespace-pre-wrap text-sm leading-relaxed">
                  {highlightActorOpportunities(narrative)}
                    </div>
              </div>
                  </div>
                );
            })()}

        {/* Actions */}
        <div className="flex justify-end space-x-4">
          <Button variant="indexOutline" onClick={() => navigate('/workflow')}>
            Run new audit
          </Button>
        </div>
      </div>
    </IndexWorkspaceShell>
  );
};

export default ScriptAnalysis;
